import { useMemo, type ReactElement } from 'react';
import { getFilterEmptyLabel, getFilterLabel } from '@/shared/copy/filters';
import { s } from '@/theme/tokens';
import { Stack } from '@/ui';
import { withStatusFilterOptionIcons } from '@/ui/composites/filters/statusFilterOptionIcons';
import { TagsFilterField } from '@/ui/composites/filters/TagsFilterField';
import { toolbarControlRadius } from '../tableSystem/tableToolbar.helpers';
import {
  normaliseMetricsToolbarTagOptions,
  type MetricsToolbarTagOption,
} from './metricsToolbarTagOptions';
import type { TableDropdownOption, TableStatusOption } from '../tableSystem/tableToolbar.types';

type MetricsToolbarFiltersProps<TStatus extends string> = {
  statusOptions: Array<TableStatusOption<TStatus>>;
  statusFilterLabel: string;
  filterStatuses: TStatus[];
  onFilterStatusesChange: (next: TStatus[]) => void;
  filterEntities: string[];
  onFilterEntitiesChange?: (next: string[]) => void;
  entityOptions?: TableDropdownOption[];
  entityLabel?: string;
  filterItems: string[];
  onFilterItemsChange?: (next: string[]) => void;
  itemOptions?: TableDropdownOption[];
  itemLabel?: string;
  filterSuites: string[];
  onFilterSuitesChange?: (next: string[]) => void;
  suiteOptions?: TableDropdownOption[];
  suiteLabel?: string;
  filterTags: string[];
  onFilterTagsChange: (next: string[]) => void;
  availableTags: MetricsToolbarTagOption[];
  filterProjects?: string[];
  onFilterProjectsChange?: (next: string[]) => void;
  projectOptions?: TableDropdownOption[];
  preserveWindowScroll: (action: () => void) => void;
};

const toToolbarPlaceholder = (label: string): string => {
  if (label.endsWith('us')) return `${label}es`;
  if (/[b-df-hj-np-tv-z]y$/i.test(label)) return `${label.slice(0, -1)}ies`;
  if (label.endsWith('s')) return label;
  return `${label}s`;
};

export const MetricsToolbarFilters = <TStatus extends string>({
  statusOptions,
  statusFilterLabel,
  filterStatuses,
  onFilterStatusesChange,
  filterEntities,
  onFilterEntitiesChange,
  entityOptions,
  entityLabel,
  filterItems,
  onFilterItemsChange,
  itemOptions,
  itemLabel,
  filterSuites,
  onFilterSuitesChange,
  suiteOptions,
  suiteLabel,
  filterTags,
  onFilterTagsChange,
  availableTags,
  filterProjects,
  onFilterProjectsChange,
  projectOptions,
  preserveWindowScroll,
}: MetricsToolbarFiltersProps<TStatus>): ReactElement => {
  const controlWidthPx = 136;
  const toolbarFieldBackgroundColor = 'var(--surface-page)';
  const tagOptions = normaliseMetricsToolbarTagOptions(availableTags);
  const showSuiteFilter = filterSuites.length > 0 || (suiteOptions?.length ?? 0) > 0;
  const showEntityFilter = filterEntities.length > 0 || (entityOptions?.length ?? 0) > 0;
  const showItemFilter = filterItems.length > 0 || (itemOptions?.length ?? 0) > 0;
  const showProjectFilter = filterProjects !== undefined && projectOptions !== undefined;
  const statusOptionsWithIcons = useMemo(
    () => withStatusFilterOptionIcons(statusOptions),
    [statusOptions],
  );

  return (
    <Stack direction="row" alignItems="center" gap="lg" sx={{ minWidth: 0 }}>
      {showSuiteFilter ? (
        <TagsFilterField
          label={suiteLabel ?? 'Suite'}
          showLabel={false}
          fontSizePx={15}
          labelPlacement="inline"
          labelControlGap={s('sm')}
          inputAriaLabel={`Filter by ${(suiteLabel ?? 'Suite').toLowerCase()}`}
          value={filterSuites}
          options={suiteOptions ?? []}
          emptyLabel={toToolbarPlaceholder(suiteLabel ?? 'Suite')}
          controlWidthPx={controlWidthPx}
          inputBackgroundColor={toolbarFieldBackgroundColor}
          inputLeftPadding={s('xs')}
          controlRadius={toolbarControlRadius}
          onChange={(next) => {
            if (!onFilterSuitesChange) return;
            preserveWindowScroll(() => {
              onFilterSuitesChange(next);
            });
          }}
        />
      ) : null}
      {showEntityFilter ? (
        <TagsFilterField
          label={entityLabel ?? 'Item'}
          showLabel={false}
          fontSizePx={15}
          labelPlacement="inline"
          labelControlGap={s('sm')}
          inputAriaLabel={`Filter by ${(entityLabel ?? 'Item').toLowerCase()}`}
          value={filterEntities}
          options={entityOptions ?? []}
          emptyLabel={toToolbarPlaceholder(entityLabel ?? 'Item')}
          controlWidthPx={controlWidthPx}
          inputBackgroundColor={toolbarFieldBackgroundColor}
          inputLeftPadding={s('xs')}
          controlRadius={toolbarControlRadius}
          onChange={(next) => {
            if (!onFilterEntitiesChange) return;
            preserveWindowScroll(() => {
              onFilterEntitiesChange(next);
            });
          }}
        />
      ) : null}
      {showItemFilter ? (
        <TagsFilterField
          label={itemLabel ?? 'Item'}
          showLabel={false}
          fontSizePx={15}
          labelPlacement="inline"
          labelControlGap={s('sm')}
          inputAriaLabel={`Filter by ${(itemLabel ?? 'Item').toLowerCase()}`}
          value={filterItems}
          options={itemOptions ?? []}
          emptyLabel={toToolbarPlaceholder(itemLabel ?? 'Item')}
          controlWidthPx={controlWidthPx}
          inputBackgroundColor={toolbarFieldBackgroundColor}
          inputLeftPadding={s('xs')}
          controlRadius={toolbarControlRadius}
          onChange={(next) => {
            if (!onFilterItemsChange) return;
            preserveWindowScroll(() => {
              onFilterItemsChange(next);
            });
          }}
        />
      ) : null}
      {statusOptions.length > 0 ? (
        <TagsFilterField
          label={statusFilterLabel}
          showLabel={false}
          fontSizePx={15}
          labelPlacement="inline"
          labelControlGap={s('sm')}
          inputAriaLabel={`Filter by ${statusFilterLabel.toLowerCase()}`}
          value={filterStatuses}
          options={statusOptionsWithIcons}
          emptyLabel={toToolbarPlaceholder(statusFilterLabel)}
          controlWidthPx={controlWidthPx}
          inputBackgroundColor={toolbarFieldBackgroundColor}
          inputLeftPadding={s('xs')}
          controlRadius={toolbarControlRadius}
          onChange={(next) => {
            preserveWindowScroll(() => {
              onFilterStatusesChange(next as TStatus[]);
            });
          }}
        />
      ) : null}
      <TagsFilterField
        label={getFilterLabel('tags')}
        showLabel={false}
        fontSizePx={15}
        labelPlacement="inline"
        labelControlGap={s('sm')}
        inputAriaLabel="Filter by tags"
        value={filterTags}
        options={tagOptions}
        emptyLabel={getFilterEmptyLabel('tags')}
        controlWidthPx={controlWidthPx}
        inputBackgroundColor={toolbarFieldBackgroundColor}
        inputLeftPadding={s('xs')}
        controlRadius={toolbarControlRadius}
        onChange={(next) => {
          preserveWindowScroll(() => {
            onFilterTagsChange(next);
          });
        }}
      />
      {showProjectFilter ? (
        <TagsFilterField
          label={getFilterLabel('project')}
          showLabel={false}
          fontSizePx={15}
          labelPlacement="inline"
          labelControlGap={s('sm')}
          inputAriaLabel="Filter by project"
          value={filterProjects}
          options={projectOptions}
          emptyLabel={getFilterEmptyLabel('projects')}
          controlWidthPx={controlWidthPx}
          inputBackgroundColor={toolbarFieldBackgroundColor}
          inputLeftPadding={s('xs')}
          controlRadius={toolbarControlRadius}
          onChange={(next) => {
            if (!onFilterProjectsChange) return;
            preserveWindowScroll(() => {
              onFilterProjectsChange(next);
            });
          }}
        />
      ) : null}
    </Stack>
  );
};
